import {
    IonCard,
    IonCardContent,
    IonCol,
    IonContent,
    IonHeader,
    IonLoading,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonRow,
    IonText,
    RefresherEventDetail,
    useIonAlert,
    useIonRouter,
} from "@ionic/react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import "./overview.css";
import { useParams } from "react-router";
import { MatchPhone } from "refsix-js-models";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Stats from "./stats";
import "./optionsMenu.css";
import {
    allMatches,
    deleteMatch,
    duplicateMatch,
} from "../../../services/matchService";
import "./fixturesSegments.css";
import {
    AlertComponent,
    HeaderComponent,
    ToastComponent,
} from "../../../components";
import OptionsPopover from "./optionsPopover";
import { routes } from "../../../route/constants";
import { mpUpdatePerson } from "../../../services/analytics/mixpanelService";
import { getFixtureResultCount } from "refsix-core";
import {
    FitnessDataProvider,
    useFitnessDataContext,
    useUserActionsCheckList,
    useUsername,
} from "../../../components/hooks";
import {
    trackEvent,
    trackPageView,
} from "../../../services/analytics/analyticsService";
import { useMatches } from "../../../components/hooks/match";
import { hasFeatureAccess } from "../../../services/subscriptionService";
import { CropMatchLength } from "./CropMatchLength";
import { MatchScore, ScoreType } from "../../../components/MatchScore";
import MatchTimings from "../../matchTimings/MatchTimings";
import {
    matchNeedsTimingsSetup,
    shouldShowPlayingPeriodsTutorial,
} from "../../../services/matchTimingsService";
import HowToVideoModal from "../../../components/HowToVideoModal";
import { VIMEO_VIDEOS } from "../../../constants/urls";
import { informationCircleOutline } from "ionicons/icons";

enum Segment {
    overview = "overview",
    teams = "teams",
    match = "match-log",
    stats = "stats",
    earnings = "earnings",
    notes = "notes",
    tags = "tags",
    wellbeing = "wellbeing",
    playerOfTheMatch = "playerOfTheMatch",
    teamBehavior = "teamBehavior",
    revs = "revs",
}

const FixturesSegments: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    return (
        <FitnessDataProvider matchId={id || ""}>
            <FixtureSegment />
        </FitnessDataProvider>
    );
};

function FixtureSegment() {
    const route = useIonRouter();
    const { t } = useTranslation();
    const { page, id } = useParams<{ page: string; id: string }>();
    const segmentEl = useRef<HTMLIonSegmentElement>(null);
    const allUserMatches = useMatches();
    const playerOfTheMatchAccess = hasFeatureAccess("playerOfTheMatch");
    const teamBehaviorAccess = hasFeatureAccess("teamBehavior");
    const [showCropMatchModal, setShowCropMatchModal] =
        useState<boolean>(false);
    const match = useSelector((state: RootState) =>
        allMatches(state.matches.matches).find((match) => {
            if (match._id === id) {
                return match;
            }
        })
    ) as MatchPhone;
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toastTitle, setToastTitle] = useState<string>("");
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [showPopover, setShowPopover] = useState<{
        open: boolean;
        event?: Event;
    }>({
        open: false,
        event: undefined,
    });
    const username = useUsername();
    const [displayAlert] = useIonAlert();
    const { reload } = useFitnessDataContext();

    const [openMatchReportModal, setOpenMatchReportModal] =
        useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [showMatchTimings, setShowMatchTimings] = useState<boolean>(false);
    // Opened from the options menu rather than auto-opened for a match that
    // has never been calibrated. Such a screen needs a way back out.
    const [timingsOpenedManually, setTimingsOpenedManually] =
        useState<boolean>(false);
    const userActionsCheckList = useUserActionsCheckList();
    const [showTimingsTutorial, setShowTimingsTutorial] =
        useState<boolean>(false);
    // Stops the tutorial re-opening if the timings effect runs again for the
    // same match after the user has dismissed it.
    const tutorialAutoShownForMatch = useRef<string | undefined>(undefined);
    // Which match the timings overlay flags currently belong to.
    const overlayMatchId = useRef<string | undefined>(undefined);
    const developerToolsEnabled = useSelector(
        (state: RootState) => state.developerTools.enabled
    );

    const isValidSegment = (value: string): value is Segment => {
        return Object.values(Segment).includes(value as Segment);
    };

    const [selectedSegment, setSelectedSegment] = useState<Segment>(
        isValidSegment(page) ? page : Segment.overview
    );

    const handleSegmentChange = (e: CustomEvent) => {
        const newSelectedSegment = e.detail.value as Segment;
        setSelectedSegment(newSelectedSegment);
        // update the address bar without redoing the react history and page animations.
        window.history.replaceState(
            null,
            "",
            `${routes.match}/${id}/${newSelectedSegment}`
        );
    };

    useEffect(() => {
        if (window.location) {
            trackPageView(window.location.pathname);
        }
    }, [selectedSegment]);

    useEffect(() => {
        console.log("page , segment", page, selectedSegment);
        // Check if 'page' is a valid segment
        if (page === selectedSegment) {
            console.log("page === selectedSegment");
            return;
        } else if (isValidSegment(page)) {
            console.log("isValidSegment(page)");
            setSelectedSegment(page);
        } else {
            console.log("else");
            setSelectedSegment(Segment.overview);
            route.push(
                `${routes.match}/${id}/${Segment.overview}`,
                "none",
                "replace"
            );
        }
    }, [page]);

    useEffect(() => {
        if (segmentEl && segmentEl.current && isValidSegment(page)) {
            setTimeout(() => {
                // get the child element of the segment button with matching value
                if (!segmentEl || !segmentEl.current) return;
                const segmentButton = segmentEl.current.querySelector(
                    `[value=${page}]`
                );
                // scroll segment into center of the view
                segmentButton?.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                    inline: "center",
                });
            }, 500);
        }
    }, [page, segmentEl, segmentEl.current]);

    const closeMatchTimings = useCallback(() => {
        setShowMatchTimings(false);
        setTimingsOpenedManually(false);
    }, []);

    const openTimingsTutorial = (trigger: "auto" | "infoIcon") => {
        trackEvent("PlayingPeriodsTutorialViewed", {
            matchId: match?._id,
            trigger: trigger,
        });
        setShowTimingsTutorial(true);
    };

    // Check if match needs timings setup (has fitness data but no periods)
    // Deliberately keyed on the match id only: the checklist is loaded into
    // redux alongside the matches, and re-running when it changes would
    // re-open the tutorial as soon as the periods are saved.
    useEffect(() => {
        if (!match) return;

        // This component is reused when the route's match id changes, so an
        // overlay left open on the previous match would render over the wrong
        // fixture. Only on an actual change - closing whenever the fitness
        // flags update would yank the screen away from someone using it.
        if (overlayMatchId.current !== match._id) {
            overlayMatchId.current = match._id;
            closeMatchTimings();
        }

        if (matchNeedsTimingsSetup(match)) {
            setShowMatchTimings(true);

            // First time only - afterwards it is available via the (i) icon
            if (
                tutorialAutoShownForMatch.current !== match._id &&
                shouldShowPlayingPeriodsTutorial(match, userActionsCheckList)
            ) {
                tutorialAutoShownForMatch.current = match._id;
                openTimingsTutorial("auto");
            }
        }
        // The fitness flags are included because a sync can land while the user
        // is already on the page: without them the screen neither auto-opens
        // nor appears in the options menu until they navigate away and back.
    }, [
        match?._id,
        match?.hasTracking,
        match?.hasHeartRate,
        closeMatchTimings,
    ]);

    if (!match) {
        return (
            <IonPage>
                <IonHeader>
                    <HeaderComponent
                        showBackButton={true}
                        title={t("general.matchDetails")}
                        backButtonTestId="match-not-exist-back"
                        defaultHref={routes.tabResults}
                    />
                </IonHeader>
                <IonContent>
                    <IonCard>
                        <IonCardContent>
                            <div data-testid="matchNotExist">
                                <IonText style={{ color: "white" }}>
                                    {t("review.error")}
                                </IonText>
                            </div>
                        </IonCardContent>
                    </IonCard>
                </IonContent>
            </IonPage>
        );
    }

    async function deleteFixture() {
        setShowPopover({ open: false, event: undefined });
        try {
            await deleteMatch(match);
            if (allUserMatches) {
                mpUpdatePerson(getFixtureResultCount(allUserMatches));
            }
            handleBack();
        } catch (err) {
            setToastTitle(t("fixture.FixtureSegments.deleteMatchError"));
            setShowToast(true);
        }
    }

    const handleDuplicateMatch = async () => {
        await duplicateMatch(match);
        setShowPopover({ open: false });
        route.goBack();
    };

    const refreshStatsForMatch = async (
        event?: CustomEvent<RefresherEventDetail>
    ) => {
        trackEvent("RefreshStatsPullDown", {
            matchId: match._id,
            hasTracking: match.hasTracking,
            hasHeartRate: match.hasHeartRate,
            gpsProcessed: match.stats?.gpsProcessed,
            heartRateProcessed: match.stats?.heartRateProcessed,
            statsVersion: match.stats?.version,
        });
        reload();
        event && event.detail.complete();
    };

    const handleAdjustPlayingPeriodsClicked = () => {
        setShowPopover({ open: false });
        setTimingsOpenedManually(true);
        setShowMatchTimings(true);
        trackEvent("PlayingPeriodsReopened", {
            matchId: match._id,
            hasTracking: match.hasTracking,
            hasHeartRate: match.hasHeartRate,
        });
    };

    const handleBack = () => {
        if (match.matchFinished) {
            route.push(routes.tabResults, "back");
        } else {
            route.push(routes.tabResults, "back");
        }
    };

    return (
        <IonPage>
            <HeaderComponent
                showBackButton={!timingsOpenedManually}
                showLeftSideBtn={timingsOpenedManually}
                leftSideBtnTxt={t("general.cancel")}
                onLeftBtnClicked={closeMatchTimings}
                title={
                    showMatchTimings
                        ? t("matchTimings.matchTimings")
                        : t("general.matchDetails")
                }
                showPopOverOptions={!showMatchTimings}
                setShowPopover={setShowPopover}
                endBtnIconName={
                    showMatchTimings ? informationCircleOutline : undefined
                }
                onClickEndButton={
                    showMatchTimings
                        ? () => openTimingsTutorial("infoIcon")
                        : undefined
                }
                endBtnTestId={
                    showMatchTimings ? "matchTimingsTutorialBtn" : undefined
                }
            />

            <HowToVideoModal
                isOpen={showTimingsTutorial}
                onDismiss={() => setShowTimingsTutorial(false)}
                video={VIMEO_VIDEOS.PLAYING_PERIODS_TUTORIAL}
                title={t("matchTimings.tutorialTitle")}
                orientation="portrait"
            />

            <OptionsPopover
                setShowPopover={setShowPopover}
                showPopover={showPopover}
                match={match}
                handleDuplicateMatch={handleDuplicateMatch}
                setShowAlert={(show) => setShowAlert(show)}
                handleCropMatchClicked={() => {
                    setShowPopover({ open: false });
                    setShowCropMatchModal(true);
                }}
                handleAdjustPlayingPeriodsClicked={
                    handleAdjustPlayingPeriodsClicked
                }
            />

            <IonContent
                scrollY={false}
                className={showMatchTimings ? "match-timings-page" : ""}
            >
                {showMatchTimings ? (
                    <MatchTimings
                        match={match}
                        onComplete={() => {
                            closeMatchTimings();
                            reload();
                        }}
                    />
                ) : (
                    <>
                        <IonRefresher
                            slot="fixed"
                            onIonRefresh={(e) => refreshStatsForMatch(e)}
                        >
                            <IonRefresherContent></IonRefresherContent>
                        </IonRefresher>
                        <IonCard className="card-container-team-names match-overview-card-margin">
                            <IonRow className="card-row-container">
                                <IonCol>
                                    <div className="team-color-wrapper">
                                        <div
                                            className="team-color-round"
                                            style={{
                                                backgroundColor:
                                                    match?.homeColor,
                                            }}
                                        >
                                            <IonText className="text-name-short">
                                                {match?.homeTeamShort}
                                            </IonText>
                                        </div>
                                    </div>
                                    <div className="name-long-wrapper">
                                        <IonText className="text-name-long">
                                            {match?.homeTeam}
                                        </IonText>
                                    </div>
                                </IonCol>

                                {match.matchFinished ||
                                match.matchInProgress ? (
                                    <div>
                                        {match.stats?.playedET[0] !== 0 &&
                                            match.stats?.playedPenalties[0] ===
                                                0 && (
                                                <div className="penalties-extra-time-container score-formatting">
                                                    <IonText
                                                        className="penalties-extra-time-text"
                                                        data-testid="penalties-extraTime-text"
                                                    >
                                                        {t(
                                                            "fixture.FixtureSummary.atExtraTime"
                                                        )}
                                                    </IonText>
                                                </div>
                                            )}
                                        {match.stats?.playedPenalties[0] ===
                                            1 && (
                                            <div className="penalties-extra-time-container score-formatting">
                                                <IonText
                                                    className="penalties-extra-time-text"
                                                    data-testid="penalties-extraTime-text"
                                                >
                                                    {t(
                                                        "fixture.FixtureSummary.atPenalties"
                                                    )}
                                                </IonText>
                                            </div>
                                        )}

                                        {match.matchInProgress &&
                                            !match.matchFinished && (
                                                <div
                                                    data-testid="match-in-progress-indicator"
                                                    className="penalties-extra-time-container score-formatting"
                                                >
                                                    <IonText
                                                        className="penalties-extra-time-text"
                                                        data-testid="penalties-extraTime-text"
                                                        style={{
                                                            textTransform:
                                                                "uppercase",
                                                        }}
                                                    >
                                                        {t(
                                                            "fixture.FixtureSummary.matchLive"
                                                        )}
                                                    </IonText>
                                                </div>
                                            )}

                                        {match.matchAbandoned && (
                                            <div className="penalties-extra-time-container score-formatting">
                                                <IonText
                                                    className="penalties-extra-time-text"
                                                    data-testid="penalties-extraTime-text"
                                                >
                                                    {t(
                                                        "model.WatchFixture.abandonedDisplayName"
                                                    )}
                                                </IonText>
                                            </div>
                                        )}

                                        <IonRow className="score-formatting">
                                            <IonText
                                                className="goals"
                                                data-testid="goalsHomeTotal"
                                            >
                                                <MatchScore
                                                    match={match}
                                                    scoreType={ScoreType.home}
                                                    color="white"
                                                />
                                            </IonText>
                                            <IonText style={{ color: "white" }}>
                                                -
                                            </IonText>
                                            <IonText
                                                className="goals"
                                                data-testid="goalsAwayTotal"
                                            >
                                                <MatchScore
                                                    match={match}
                                                    scoreType={ScoreType.away}
                                                    color="white"
                                                />
                                            </IonText>
                                        </IonRow>
                                        {match.stats?.playedPenalties[0] ===
                                            1 && (
                                            <IonRow className="score-formatting penalty-score">
                                                <IonText>
                                                    {
                                                        match.stats
                                                            ?.penaltyShotHomeScored
                                                    }
                                                </IonText>
                                                <IonText>-</IonText>
                                                <IonText>
                                                    {
                                                        match.stats
                                                            ?.penaltyShotAwayScored
                                                    }
                                                </IonText>
                                            </IonRow>
                                        )}
                                    </div>
                                ) : (
                                    <IonText>-</IonText>
                                )}
                                <IonCol>
                                    <div className="team-color-wrapper">
                                        <div
                                            className="team-color-round"
                                            style={{
                                                backgroundColor:
                                                    match?.awayColor,
                                            }}
                                        >
                                            <IonText className="text-name-short">
                                                {match?.awayTeamShort}
                                            </IonText>
                                        </div>
                                    </div>
                                    <div className="name-long-wrapper">
                                        <IonText className="text-name-long">
                                            {match?.awayTeam}
                                        </IonText>
                                    </div>
                                </IonCol>
                            </IonRow>
                        </IonCard>

                        <Stats match={match} />

                        <IonLoading isOpen={loading} />

                        <AlertComponent
                            testId="delete-match-alert"
                            showAlert={showAlert}
                            onDonePressed={() => deleteFixture()}
                            dismissAlert={() => setShowAlert(false)}
                            showAlternativeButton
                            buttonDone={t("fixture.FixtureIncident.confirm")}
                            title={`${t("general.delete")} ${t(
                                "general.match"
                            )}`}
                            subTitle={t(
                                "fixture.FixtureSegments.deleteMatchDesc"
                            )}
                            buttonAltText={t("general.cancel")}
                        />
                        <ToastComponent
                            isOpen={showToast}
                            position="bottom"
                            message={toastTitle}
                            onDismiss={() => setShowToast(false)}
                        />
                        {showCropMatchModal && (
                            <CropMatchLength
                                isModalOpen={showCropMatchModal}
                                onDismiss={() => setShowCropMatchModal(false)}
                                match={match}
                            />
                        )}
                    </>
                )}
            </IonContent>
        </IonPage>
    );
}

export default FixturesSegments;
